import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BRAND_LOGO } from '../data/homeImages';
import { SITE } from '../data/site';
import { CATEGORY_LIST, EXTERIOR_MOODS, searchCatalog } from '../data/catalog';
import { ArrowRight, ChevronDown, Close, Mail, Menu, Search, MapPin } from './ui/Icons';

/* ------------------------------------------------------------------ */
/* Menu model                                                          */
/* ------------------------------------------------------------------ */

type MenuLink = { label: string; to: string; hint?: string };
type MenuColumn = { title: string; links: MenuLink[] };
/** `cutout` = transparent pack shot, shown whole above the copy instead of as a cropped backdrop. */
type MenuFeature = { eyebrow: string; title: string; copy: string; to: string; image: string; cta: string; cutout?: boolean };
type MenuItem = { key: string; label: string; to?: string; columns?: MenuColumn[]; feature?: MenuFeature };

const PRODUCT_COLUMNS: MenuColumn[] = [
  {
    title: 'By surface',
    // Getter: the count is read at render, after any admin catalogue has loaded.
    links: CATEGORY_LIST.map((c) => ({ label: c.shortName, to: c.route, get hint() { return `${c.products.length} products`; } })),
  },
  {
    title: 'Popular products',
    links: [
      { label: 'Marmovella stone finish', to: '/Marmovella', hint: 'New' },
      { label: 'Easy Wash Luxury Emulsion', to: '/InteriorWallPaint/EasyWashLuxuryEmulsion' },
      { label: 'Silk Shine Pearl Gloss', to: '/InteriorWallPaint/SilkShine_PearlGloss' },
      { label: 'Titanium One', to: '/ExteriorWallPaint/TitaniumOne' },
      { label: 'Ultra WRP Exterior Emulsion', to: '/ExteriorWallPaint/UltraWRPExteriorEmulsion' },
      { label: 'Damp Proof Wall Putty', to: '/UnderCoat/DampProofWallPutty' },
      { label: 'Cool Damp Proof', to: '/WaterProofing/RoofDampGuard' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'Technical data sheets', to: '/TDS' },
      { label: 'Paint calculator', to: '/CalculateNow' },
      { label: 'Wall textures', to: '/textures' },
      { label: 'Painting guides', to: '/guides' },
    ],
  },
];

const MENU: MenuItem[] = [
  {
    key: 'products',
    label: 'Products',
    columns: PRODUCT_COLUMNS,
    feature: {
      eyebrow: 'New launch',
      title: 'Marmovella — Italian mineral stone finish',
      copy: 'Classico, Granito and Lusso: 16 mineral shades with an 11-year system warranty.',
      to: '/Marmovella',
      image: '/images/marmovella/marmovella-buckets-cutout.webp',
      cutout: true,
      cta: 'Discover Marmovella',
    },
  },
  {
    key: 'colours',
    label: 'Colours',
    columns: [
      {
        title: 'Explore',
        links: [
          { label: 'Colour catalogue', to: '/colours/catalogue', hint: 'Curated colour series' },
          { label: 'Interior shades', to: '/InteriorWallPaint/Shades', hint: 'Popular shades by room' },
          { label: 'Colour combinations', to: '/colours/combinations' },
          { label: 'Colours by room', to: '/colours/rooms' },
        ],
      },
      {
        title: 'Exterior moods',
        links: EXTERIOR_MOODS.map((m) => ({ label: m.title, to: m.to })),
      },
      {
        title: 'Tools',
        links: [
          { label: 'Colour visualiser', to: '/visualizer' },
          { label: 'Colour guide', to: '/color-guide' },
          { label: 'Request a sample', to: '/sample-request' },
        ],
      },
    ],
    feature: {
      eyebrow: 'Colour Mode',
      title: 'Over a thousand shades, matched at the counter',
      copy: 'Bring a swatch, a photo or an idea — we tint it exactly.',
      to: '/colours/catalogue',
      image: '/images/Interior-Paints.webp',
      cta: 'Browse the catalogue',
    },
  },
  {
    key: 'services',
    label: 'Services',
    columns: [
      {
        title: 'Painting services',
        links: [
          { label: 'Safe painting service', to: '/services/safe-painting' },
          { label: 'Waterproofing solutions', to: '/services/waterproofing' },
          { label: 'Book a painter', to: '/services/book-painter' },
        ],
      },
      {
        title: 'Advice & planning',
        links: [
          { label: 'Colour consultancy', to: '/services/colour-consultancy' },
          { label: 'Paint calculator', to: '/CalculateNow' },
          { label: 'Find a store or dealer', to: '/store-locator' },
        ],
      },
    ],
    feature: {
      eyebrow: 'Expert help',
      title: 'Get an on-site consultation',
      copy: 'Surface assessment, product specification and a clear estimate before a single tin is opened.',
      to: '/services',
      image: '/images/web/auto/tf-livingroom.jpg',
      cta: 'See all services',
    },
  },
  {
    key: 'company',
    label: 'Company',
    columns: [
      {
        title: 'About Vertex',
        links: [
          { label: 'Our story', to: '/AboutUs/OurStory' },
          { label: 'Certification & quality', to: '/AboutUs/OurStory#quality' },
          { label: 'Contact us', to: '/contact' },
        ],
      },
      {
        title: 'Stories',
        links: [
          { label: 'Blog', to: '/blog' },
          { label: 'Videos', to: '/videos' },
          { label: 'Customer testimonials', to: '/testimonials' },
        ],
      },
    ],
    feature: {
      eyebrow: 'Since 2007',
      title: 'Award-winning water-based paint manufacturer',
      copy: 'Recognised as Best Water-Based Paint Manufacturer at the PaintVision Awards.',
      to: '/AboutUs/OurStory',
      image: '/images/certificate.jpeg',
      cta: 'Read our story',
    },
  },
];

/* ------------------------------------------------------------------ */
/* Search overlay                                                       */
/* ------------------------------------------------------------------ */

const SearchOverlay = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(() => searchCatalog(q, 8), [q]);

  useEffect(() => {
    if (open) {
      setQ('');
      setTimeout(() => inputRef.current?.focus(), 50);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!open) return null;

  const go = (to: string) => {
    onClose();
    navigate(to);
  };

  const quick = [
    { label: 'Marmovella', to: '/Marmovella' },
    { label: 'Interior paints', to: '/InteriorWallPaint' },
    { label: 'Exterior paints', to: '/ExteriorWallPaint' },
    { label: 'Waterproofing', to: '/WaterProofingAndConstructionChemicals' },
    { label: 'Colour catalogue', to: '/colours/catalogue' },
    { label: 'Paint calculator', to: '/CalculateNow' },
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-brand-navy/70 backdrop-blur-sm animate-fadeIn" onClick={onClose} role="dialog" aria-modal="true" aria-label="Search">
      <div className="container pt-20 sm:pt-28" onClick={(e) => e.stopPropagation()}>
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lift overflow-hidden">
          <form
            className="flex items-center gap-3 px-5 py-4 border-b border-brand-line"
            onSubmit={(e) => {
              e.preventDefault();
              if (results[0]) go(results[0].route);
            }}
          >
            <Search size={20} className="text-brand-muted flex-shrink-0" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products — e.g. “putty”, “exterior emulsion”, “primer”"
              className="flex-1 text-base sm:text-lg outline-none placeholder:text-brand-muted/70 bg-transparent"
              aria-label="Search products"
            />
            <button type="button" onClick={onClose} className="p-2 rounded-lg text-brand-muted hover:bg-brand-surface" aria-label="Close search">
              <Close size={18} />
            </button>
          </form>

          <div className="max-h-[60vh] overflow-y-auto">
            {q && results.length === 0 && <p className="px-5 py-8 text-sm text-brand-muted text-center">No products match “{q}”. Try a surface, like “metal” or “roof”.</p>}
            {results.length > 0 && (
              <ul className="py-2">
                {results.map((r) => (
                  <li key={r.route}>
                    <button onClick={() => go(r.route)} className="w-full flex items-center gap-4 px-5 py-3 hover:bg-brand-surface text-left transition-colors">
                      <span className="w-12 h-12 rounded-lg bg-brand-surface flex items-center justify-center overflow-hidden flex-shrink-0">
                        {r.image ? <img src={r.image} alt="" className="h-10 w-auto object-contain" /> : <span className="w-6 h-8 rounded bg-brand-blue/20" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-brand-ink truncate">{r.title}</span>
                        <span className="block text-xs text-brand-muted truncate">{r.features[0]}</span>
                      </span>
                      <ArrowRight size={14} className="text-brand-muted flex-shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!q && (
              <div className="px-5 py-5">
                <p className="text-xs font-semibold tracking-wider uppercase text-brand-muted">Quick links</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {quick.map((l) => (
                    <button key={l.to} onClick={() => go(l.to)} className="vp-chip vp-chip-outline hover:border-brand-blue hover:text-brand-blue transition-colors">
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Mega menu panel                                                      */
/* ------------------------------------------------------------------ */

const MegaPanel = ({ item, onNavigate }: { item: MenuItem; onNavigate: () => void }) => (
  <div className="absolute left-0 right-0 top-full pt-2 animate-fadeIn">
    <div className="container">
      <div className="bg-white rounded-2xl shadow-lift border border-brand-line overflow-hidden">
        <div className="grid lg:grid-cols-12">
          <div className={`p-8 lg:p-9 grid gap-8 ${item.columns && item.columns.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'} lg:col-span-8`}>
            {item.columns?.map((col) => (
              <div key={col.title}>
                <h3 className="text-[11px] font-bold tracking-[.16em] uppercase text-brand-muted mb-4">{col.title}</h3>
                <ul className="space-y-1">
                  {col.links.map((l) => (
                    <li key={l.to + l.label}>
                      <Link to={l.to} onClick={onNavigate} className="group flex items-baseline justify-between gap-3 py-1.5 -mx-2 px-2 rounded-lg hover:bg-brand-surface transition-colors">
                        <span className="text-[14.5px] font-medium text-brand-ink group-hover:text-brand-blue transition-colors">{l.label}</span>
                        {l.hint && <span className="text-xs text-brand-muted whitespace-nowrap">{l.hint}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {item.feature && (
            <Link to={item.feature.to} onClick={onNavigate} className="group relative lg:col-span-4 min-h-[260px] overflow-hidden bg-brand-navy text-white">
              {item.feature.cutout ? (
                <img src={item.feature.image} alt="" className="block w-full h-auto px-7 pt-7 drop-shadow-[0_18px_24px_rgba(0,0,0,.45)] transition-transform duration-700 group-hover:scale-105" />
              ) : (
                <>
                  <img src={item.feature.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/10" />
                </>
              )}
              <div className={`relative flex flex-col justify-end p-7 ${item.feature.cutout ? '' : 'h-full'}`}>
                <span className="vp-eyebrow vp-eyebrow-light">{item.feature.eyebrow}</span>
                <h3 className="mt-3 text-xl font-semibold leading-snug !text-white">{item.feature.title}</h3>
                <p className="mt-2 text-sm text-white/75 leading-relaxed">{item.feature.copy}</p>
                <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-brand-gold group-hover:gap-3 transition-all">
                  {item.feature.cta} <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          )}
        </div>
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Navbar                                                               */
/* ------------------------------------------------------------------ */

const Navbar = () => {
  const [active, setActive] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<string | null>('products');
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close everything on route change.
  useEffect(() => {
    setActive(null);
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // Escape closes whichever panel is open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActive(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  const open = useCallback((key: string) => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setActive(key);
  }, []);
  const scheduleClose = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setActive(null), 120);
  }, []);

  const closeSearch = useCallback(() => setSearchOpen(false), []);

  return (
    <>
      <header id="main-header" className={`sticky top-0 z-50 bg-white transition-shadow duration-300 ${scrolled ? 'shadow-[0_8px_30px_-18px_rgba(16,22,47,.35)]' : ''}`}>
        {/* Top bar */}
        <div className={`gradient-bg text-white transition-all duration-300 overflow-hidden ${scrolled ? 'max-h-0' : 'max-h-10'}`}>
          <div className="container flex items-center justify-between h-9 text-[12.5px]">
            <p className="font-medium tracking-wide text-white/95 truncate">
              {SITE.announcement || (
                <>
                  Award-winning water-based paints
                  <span className="hidden sm:inline text-white/60"> — made in India since 2007</span>
                </>
              )}
            </p>
            <div className="hidden sm:flex items-center gap-5 text-white/85">
              <a href={`mailto:${SITE.supportEmail}`} className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
                <Mail size={13} /> {SITE.supportEmail}
              </a>
              <span className="w-px h-3.5 bg-white/25" aria-hidden="true" />
              <Link to="/store-locator" className="inline-flex items-center gap-1.5 hover:text-white transition-colors">
                <MapPin size={13} /> Store locator
              </Link>
            </div>
          </div>
        </div>

        {/* Main bar */}
        <div className="relative border-b border-brand-line" onMouseLeave={scheduleClose}>
          <div className="container flex items-center justify-between gap-4 h-16 lg:h-[76px]">
            <Link to="/" className="flex-shrink-0" aria-label="Vertex Paints — home">
              <img src={BRAND_LOGO} alt="Vertex Paints" width={1095} height={300} className="h-10 sm:h-11 lg:h-14 w-auto object-contain" />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
              {MENU.map((item) => (
                <div key={item.key} onMouseEnter={() => open(item.key)} className="relative">
                  <button
                    className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-[15px] font-semibold transition-colors ${
                      active === item.key ? 'text-brand-blue bg-brand-surface' : 'text-brand-ink hover:text-brand-blue'
                    }`}
                    aria-expanded={active === item.key}
                    aria-haspopup="true"
                    onClick={() => (active === item.key ? setActive(null) : open(item.key))}
                  >
                    {item.label}
                    <ChevronDown size={14} className={`transition-transform ${active === item.key ? 'rotate-180' : ''}`} />
                  </button>
                </div>
              ))}
              <NavLink to="/contact" className={({ isActive }) => `px-4 py-2.5 rounded-lg text-[15px] font-semibold transition-colors ${isActive ? 'text-brand-blue' : 'text-brand-ink hover:text-brand-blue'}`}>
                Contact
              </NavLink>
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button onClick={() => setSearchOpen(true)} className="p-2.5 rounded-lg text-brand-ink hover:bg-brand-surface transition-colors" aria-label="Search products">
                <Search size={20} />
              </button>
              <Link to="/CalculateNow" className="hidden sm:inline-flex vp-btn vp-btn-gold vp-btn-sm">
                Calculate now
                <ArrowRight size={14} />
              </Link>
              <button className="lg:hidden p-2.5 rounded-lg text-brand-ink hover:bg-brand-surface" onClick={() => setMobileOpen((v) => !v)} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen}>
                {mobileOpen ? <Close size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>

          {/* Mega menu */}
          {active && (
            <div onMouseEnter={() => open(active)} className="hidden lg:block">
              <MegaPanel item={MENU.find((m) => m.key === active)!} onNavigate={() => setActive(null)} />
            </div>
          )}
        </div>
      </header>

      {/* Backdrop for mega menu */}
      {active && <div className="hidden lg:block fixed inset-0 z-40 bg-brand-navy/30 backdrop-blur-[2px] animate-fadeIn" onClick={() => setActive(null)} aria-hidden="true" />}

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-brand-navy/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="relative ml-auto w-[88%] max-w-sm h-full bg-white shadow-lift flex flex-col animate-[vpSlideIn_.3s_ease-out]">
            <div className="flex items-center justify-between px-5 h-16 border-b border-brand-line">
              <img src={BRAND_LOGO} alt="Vertex Paints" width={1095} height={300} className="h-10 w-auto" />
              <button onClick={() => setMobileOpen(false)} className="p-2 rounded-lg hover:bg-brand-surface" aria-label="Close menu">
                <Close size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {MENU.map((item) => (
                <div key={item.key} className="border-b border-brand-line">
                  <button
                    className="w-full flex items-center justify-between px-5 py-4 text-left font-semibold text-brand-ink"
                    onClick={() => setMobileSection(mobileSection === item.key ? null : item.key)}
                    aria-expanded={mobileSection === item.key}
                  >
                    {item.label}
                    <ChevronDown size={16} className={`transition-transform ${mobileSection === item.key ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileSection === item.key && (
                    <div className="px-5 pb-4 space-y-5">
                      {item.columns?.map((col) => (
                        <div key={col.title}>
                          <p className="text-[11px] font-bold tracking-[.16em] uppercase text-brand-muted mb-2">{col.title}</p>
                          <ul className="space-y-1">
                            {col.links.map((l) => (
                              <li key={l.to + l.label}>
                                <Link to={l.to} className="block py-1.5 text-[15px] text-brand-ink/90 hover:text-brand-blue" onClick={() => setMobileOpen(false)}>
                                  {l.label}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Link to="/contact" className="block px-5 py-4 font-semibold text-brand-ink border-b border-brand-line" onClick={() => setMobileOpen(false)}>
                Contact
              </Link>
              <div className="p-5 space-y-3">
                <Link to="/CalculateNow" className="vp-btn vp-btn-gold w-full" onClick={() => setMobileOpen(false)}>
                  Calculate your cost <ArrowRight />
                </Link>
                <Link to="/store-locator" className="vp-btn vp-btn-outline w-full" onClick={() => setMobileOpen(false)}>
                  <MapPin size={16} /> Find a store
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <SearchOverlay open={searchOpen} onClose={closeSearch} />
    </>
  );
};

export default Navbar;
